import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {ChevronRightIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useState} from 'react'
import type {PendingComment} from '../../page-data/payloads/thread-previews'
import type {Author, Comments} from '@github-ui/diff-lines/conversation-types'
import {ConversationHeader} from './ConversationHeader'
import {PreviewAuthors} from './PreviewAuthors'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useMarkerAndDiffNavigationActions} from '../../stores/selectors'
import {addUrlToHistoryStack} from '@github-ui/history'
import styles from './PendingCommentPreview.module.css'

interface PendingCommentPreviewProps {
  pendingComment: PendingComment
  onNavigateToDiffComment: (threadId: string) => void
  tabSize?: number
}

export const PendingCommentPreview = memo(function PendingCommentPreview({
  onNavigateToDiffComment,
  pendingComment,
  tabSize,
}: PendingCommentPreviewProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const {commentID, thread} = pendingComment
  const {navigateToSelectedComment} = useMarkerAndDiffNavigationActions()
  const commentAuthors = extractCommentAuthors(commentID.toString(), thread.commentsData ?? {comments: []})
  const previousCommentCount = thread.commentsData?.comments?.length ? thread.commentsData.comments.length - 1 : 0
  const previousCommentText = `${previousCommentCount} previous ${previousCommentCount === 1 ? 'comment' : 'comments'}`

  const navigateToComment = useCallback(() => {
    navigateToSelectedComment(commentID)
    const currentUrl = window.location.pathname + window.location.search
    addUrlToHistoryStack(`${currentUrl}#r${commentID}`)
    onNavigateToDiffComment(commentID.toString())
  }, [commentID, navigateToSelectedComment, onNavigateToDiffComment])

  return (
    <div className="border rounded-2 d-flex flex-column">
      <ConversationHeader
        isCollapsed={isCollapsed}
        isOutdated={thread.isOutdated || false}
        isResolved={thread.isResolved || false}
        line={thread.subject?.endLine}
        path={thread.path ?? ''}
        onNavigateToDiffComment={navigateToComment}
        onToggleCollapsed={() => setIsCollapsed(collapsed => !collapsed)}
        threadId={thread.id}
        firstCommentId={commentID}
      />
      {!isCollapsed && (
        <>
          <div className="border-bottom borderColor-muted overflow-x-auto">
            <StaticUnifiedDiffPreview
              diffTableClassName="border-0"
              diffLines={thread.subject?.diffLines}
              className="m-0 border-0"
              tabSize={tabSize || 4}
            />
          </div>
          {previousCommentCount > 0 && (
            <div className="my-2 px-2">
              <Button
                aria-label="View comment in diff"
                size="small"
                trailingVisual={ChevronRightIcon}
                variant="invisible"
                onClick={navigateToComment}
              >
                <div className="d-flex flex-row flex-justify-start flex-items-center gap-2">
                  <span>{previousCommentText}</span>
                  <PreviewAuthors commentAuthors={commentAuthors} />
                </div>
              </Button>
            </div>
          )}
          <VerifiedHTMLDiv
            className={clsx('markdown-body', styles.commentBody)}
            html={extractCommentBodyHTML(pendingComment)}
          />
        </>
      )}
    </div>
  )
})

export function extractCommentAuthors(commentId: string, commentsData: Comments): Author[] {
  const uniqueAuthors = new Set<string>()
  const commentsArray = commentsData?.comments ?? []

  const extractedAuthors = commentsArray.reduce((authors, comment) => {
    // Skip the current comment (match by id)
    if (!comment || comment.id === commentId) return authors

    const login = comment.author?.login ?? ''
    const avatarUrl = comment.author?.avatarUrl ?? ''
    const id = comment.author?.id ?? ''
    const url = comment.author?.url ?? ''

    // Use login as the uniqueness key (empty string allowed)
    if (!uniqueAuthors.has(login)) {
      authors.push({
        avatarUrl,
        login,
        id,
        url,
      })
      uniqueAuthors.add(login)
    }

    return authors
  }, [] as Author[])

  return extractedAuthors
}

function extractCommentBodyHTML(pendingComment: PendingComment): SafeHTMLString {
  const commentsArray = pendingComment.thread.commentsData?.comments ?? []
  const matchedComment = commentsArray.find(comment => comment && comment.databaseId === pendingComment.commentID)
  const html = matchedComment?.bodyHTML ?? ''

  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
  return html as SafeHTMLString
}
